import { inject, Injectable, signal } from '@angular/core';
import {
	catchError,
	firstValueFrom,
	from,
	map,
	mergeMap,
	of,
	toArray,
	type Observable
} from 'rxjs';
import type {
	PartCodingApproval,
	PartCodingQueueLine
} from '../models/part-coding.model';
import type { PartCodingDecisionRequest } from '../models/part-coding';
import { PartCodingHttpService } from './part-coding-http.service';
import { ToastService } from './toast.service';

const CONFIRM_CONCURRENCY = 4;

export interface HistoryWriteOutcome {
	line: PartCodingQueueLine;
	// Null when saved. Otherwise the API's skip reason, or 'request-failed'.
	skipReason: string | null;
}

// Lines an ISR has exported. Loaded from the API, so an admin on another
// computer sees the same queue.
@Injectable({
	providedIn: 'root'
})
export class PartCodingQueueService {
	private readonly partCodingHttp = inject(PartCodingHttpService);
	private readonly toastService = inject(ToastService);

	public readonly lines = signal<readonly PartCodingQueueLine[]>([]);

	public async load(): Promise<void> {
		try {
			const lines = await firstValueFrom(this.partCodingHttp.queue());
			this.lines.set(lines.map((line) => this.withDefaults(line)));
		} catch {
			this.toastService.showErrorToast('Could not load the admin queue.');
		}
	}

	public replace(lines: readonly PartCodingQueueLine[]): void {
		this.lines.set(lines);
	}

	private withDefaults(line: PartCodingQueueLine): PartCodingQueueLine {
		const stored = line as Partial<PartCodingQueueLine>;
		return {
			...line,
			candidateFamily: '',
			candidates: stored.candidates ?? [],
			repCheck: stored.repCheck ?? 'accepted',
			estimateId: stored.estimateId ?? null
		};
	}

	// Saves the decision first, then writes approved lines to the customer's
	// history. A skipped or failed history write never undoes the approval.
	public async decide(
		ids: ReadonlySet<string>,
		approval: PartCodingApproval,
		decidedBy: string,
		decidedAt: number
	): Promise<readonly HistoryWriteOutcome[]> {
		const decided: PartCodingQueueLine[] = [];
		this.lines.update((all) =>
			all.map((line) => {
				if (!ids.has(line.id) || line.approval) return line;
				const next = { ...line, approval, decidedBy, decidedAt };
				decided.push(next);
				return next;
			})
		);

		const toRecord = decided.filter(
			(line) => line.codingLineId != null || line.approval === 'approved'
		);
		if (!toRecord.length) return [];
		return firstValueFrom(
			from(toRecord).pipe(
				mergeMap(
					(line) => this.recordDecision(line),
					CONFIRM_CONCURRENCY
				),
				toArray()
			)
		);
	}

	private recordDecision(
		line: PartCodingQueueLine
	): Observable<HistoryWriteOutcome> {
		if (line.codingLineId != null && line.approval) {
			return this.partCodingHttp
				.recordDecision(this.decisionBody(line))
				.pipe(
					map((response) => ({
						line,
						skipReason: this.historySkip(line, response)
					})),
					catchError(() =>
						of({
							line,
							skipReason:
								line.approval === 'approved'
									? 'request-failed'
									: null
						})
					)
				);
		}

		if (line.approval !== 'approved') return of({ line, skipReason: null });
		return this.writeToHistory(line);
	}

	private decisionBody(line: PartCodingQueueLine): PartCodingDecisionRequest {
		const approval = line.approval ?? 'rejected';
		const body: PartCodingDecisionRequest = {
			lineId: line.codingLineId ?? 0,
			approval
		};
		if (line.source === 'Manual' && line.partCode.trim()) {
			body.partCode = line.partCode;
			body.rkDescription = line.rkDescription;
			body.reason = line.reason;
		}
		return body;
	}

	private historySkip(
		line: PartCodingQueueLine,
		response: {
			historySaved: boolean;
			historyReason: string | null;
		}
	): string | null {
		if (line.approval !== 'approved' || response.historySaved) return null;
		return response.historyReason ?? 'skipped';
	}

	private writeToHistory(
		line: PartCodingQueueLine
	): Observable<HistoryWriteOutcome> {
		return this.partCodingHttp
			.confirmMatch({
				parentCompanyId: line.historyParentCompanyId,
				customerDescription: line.customerDescription,
				rkPartCode: line.partCode,
				rkDescription: line.rkDescription
			})
			.pipe(
				map((response) => ({
					line,
					skipReason: response.saved
						? null
						: (response.reason ?? 'skipped')
				})),
				catchError(() => of({ line, skipReason: 'request-failed' }))
			);
	}

}
